import { useState, useEffect, useMemo, useRef } from "react";
import UsuarioService from "../../services/resource/usuarioService";
import InputForm from "../../componentes/input/input";
import { MultiSelect } from "primereact/multiselect";
import { toast } from 'react-toastify';
import { Loading } from "../../componentes/loading/loading";
import { Dropdown } from "primereact/dropdown";
import { reduzirNome } from "../../utils/reduzirNome";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import FilialService from "../../services/resource/filialService";
import Button from "../../componentes/button/button";
import { Menu } from "primereact/menu";
import Pagination from "../../componentes/pagination/Pagination";
import FuncionarioAutoComplete from "../../componentes/funcionario/FuncionarioAutoComplete";
import { PROCESSO_REGISTRY } from "../../utils/solicitacoes/processoRegistry";

const initialValues = {
    id: "",
    nome: "",
    nomeUsuario: "",
    filialId: null,
    filialNome: "",
    email: "",
    senha: "",
    confirmSenha: "",
    ativo: true,
    perfis: [],
    tiposProcessoGerenciados: [],
    funcionarioId: null,
    funcionarioNome: "",
    acessoMydash: true,
    acessoMydashSolicitacoes: true
};

const PERFIS_MAP = {
    1: "ADMIN",
    2: "ANALISTA",
    3: "CONTADOR",
    4: "RH",
    5: "SUPRIMENTOS",
    6: "DIRETOR",
    7: "LOGISTICA",
    8: "BP",
    9: "COMISSOES",
    10: "COMISSOES CAXIAS",
    11: "MOTORISTA",
    12: "AUXILIAR DE ENTREGA",
    13: "FINANCEIRO",
};

const perfisOptions = Object.entries(PERFIS_MAP).map(([id, label]) => ({
    label,
    value: label,
}));

// Gestor do Processo: usuário vê todas as solicitações/tarefas dos TipoProcesso aqui marcados,
// mesmo sem ter participado (só visualização — não ganha permissão para agir). Os values são os
// próprios nomes do enum TipoProcesso, sem indireção de código (diferente de Perfis).
const gestorProcessoOptions = Object.entries(PROCESSO_REGISTRY).map(([tipo, info]) => ({
    label: info.label,
    value: tipo,
}));

// Rótulo + controle empilhados — mesmo padrão usado nos filtros de Central de Tarefas, pra cada
// campo ocupar sua própria célula do grid em vez de disputar espaço numa única linha.
function CampoFiltro({ label, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-foreground/70">{label}</label>
            {children}
        </div>
    );
}

// Célula "Funcionário" da tabela: mostra o nome vinculado (ou "—") com um lápis discreto no hover;
// clicar troca pra um autocomplete de busca. Só um "onSalvar" (o pai decide como persistir, com
// otimista + rollback) — este componente só cuida da UI de edição em si. O cargo tem sua própria
// coluna na tabela (evita repetir o mesmo dado duas vezes).
function FuncionarioCelula({ usuario, onSalvar }) {
    const [editando, setEditando] = useState(false);
    const valorAtual = usuario.funcionarioId ? { id: usuario.funcionarioId, nome: usuario.funcionarioNome } : null;

    if (!editando) {
        return (
            <button
                type="button"
                onClick={() => setEditando(true)}
                title="Alterar funcionário vinculado"
                className="group inline-flex items-center gap-1.5 text-left max-w-[160px]"
            >
                <span className={`truncate ${usuario.funcionarioNome ? "text-foreground" : "text-muted-foreground italic"}`}>
                    {usuario.funcionarioNome || "—"}
                </span>
                <i className="fa fa-pencil text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0" />
            </button>
        );
    }

    return (
        <div className="flex items-center gap-1 min-w-[190px]">
            <FuncionarioAutoComplete
                value={valorAtual}
                onChange={funcionario => {
                    setEditando(false);
                    onSalvar(funcionario);
                }}
                placeholder="Buscar funcionário..."
                inputClassName="w-full h-8 pl-2 pr-7 text-xs border border-input rounded-md bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                autoFocus
            />
            <button
                type="button"
                title="Cancelar"
                onClick={() => setEditando(false)}
                className="text-muted-foreground hover:text-foreground p-1 shrink-0"
            >
                <i className="fa fa-undo text-[10px]" />
            </button>
        </div>
    );
}

export default function UsuarioConfig() {
    const [usuarios, setUsuarios] = useState([]);
    const [nomeFilter, setNomeFilter] = useState("");
    const [emailFilter, setEmailFilter] = useState("");
    const [filialFilter, setFilialFilter] = useState(null);
    const [cargoFilter, setCargoFilter] = useState(null);
    const [perfilFilter, setPerfilFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState("active");
    const [acessoMydashFilter, setAcessoMydashFilter] = useState("all");
    const [acessoSolicitacaoFilter, setAcessoSolicitacaoFilter] = useState("all");
    const [paginatedUsuarios, setPaginatedUsuarios] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [ativeModalOpen, setAtiveModalOpen] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [usuarioForm, setUsuarioForm] = useState(initialValues);
    const [usuarioDelete, setUsuarioDelete] = useState(initialValues);
    const [filiais, setFiliais] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [sessoesModalOpen, setSessoesModalOpen] = useState(false);
    const [usuarioSessoes, setUsuarioSessoes] = useState(initialValues);
    const [sessoes, setSessoes] = useState([]);
    const [carregandoSessoes, setCarregandoSessoes] = useState(false);
    const [revogandoSessaoId, setRevogandoSessaoId] = useState(null);
    const [revogandoTodas, setRevogandoTodas] = useState(false);

    const menu = useRef(null);

    const usuarioService = new UsuarioService();
    const filialService = new FilialService();

    const isNovoUsuario = usuarioForm.id === null || usuarioForm.id === "";

    useEffect(() => {
        renderUsersTable();
        document.title = "MyDash | Usuários";

        filialService.listar()
            .then(res => setFiliais(res.data))
            .catch(error => {
                console.error("Erro ao listar filiais", error);
                toast.error("Erro ao carregar filiais");
            });
    }, []);

    function renderUsersTable() {
        setLoading(true);
        usuarioService.listarUsuarios()
            .then(response => {
                setUsuarios(response.data);
            })
            .catch(error => {
                console.error("Erro ao listar usuários", error);
                toast.error("Erro ao carregar usuários");
            })
            .finally(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 400);
            });
    }

    const filiaisOptions = useMemo(() => filiais.map((filial) => ({
        label: filial.nome,
        value: filial.id,
    })), [filiais]);

    // Cargos derivados dos próprios usuários carregados (via funcionário vinculado) — não existe
    // uma lista mestre separada de cargos aqui, então a opção de filtro é só o que já apareceu.
    const cargosOptions = useMemo(() => {
        const vistos = new Set(usuarios.map(u => u.funcionarioCargo).filter(Boolean));
        return Array.from(vistos).sort().map(cargo => ({ label: cargo, value: cargo }));
    }, [usuarios]);

    // Memoizado: alimenta o <Pagination>, que chama currentData(...) durante o próprio render —
    // sem useMemo aqui, a referência mudaria a cada render e entraria em loop de atualização.
    const filteredUsuarios = useMemo(() => usuarios.filter(u => {
        const matchesNome = !nomeFilter ||
            u.nome?.toLowerCase().includes(nomeFilter.toLowerCase()) ||
            u.nomeUsuario?.toLowerCase().includes(nomeFilter.toLowerCase());
        const matchesEmail = !emailFilter ||
            u.email?.toLowerCase().includes(emailFilter.toLowerCase());
        const matchesFilial = !filialFilter || String(u.filialId) === String(filialFilter);
        const matchesCargo = !cargoFilter || u.funcionarioCargo === cargoFilter;
        const matchesPerfil = perfilFilter.length === 0 ||
            perfilFilter.some((perfil) => u.perfis?.includes(perfil));
        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "active" && u.ativo) ||
            (statusFilter === "inactive" && !u.ativo);
        // null no backend = sem restrição = acesso liberado (mesma regra usada no switch da tabela).
        const temAcessoMydash = u.acessoMydash ?? true;
        const temAcessoSolicitacao = u.acessoMydashSolicitacoes ?? true;
        const matchesAcessoMydash = acessoMydashFilter === "all" ||
            (acessoMydashFilter === "yes" && temAcessoMydash) ||
            (acessoMydashFilter === "no" && !temAcessoMydash);
        const matchesAcessoSolicitacao = acessoSolicitacaoFilter === "all" ||
            (acessoSolicitacaoFilter === "yes" && temAcessoSolicitacao) ||
            (acessoSolicitacaoFilter === "no" && !temAcessoSolicitacao);
        return matchesNome && matchesEmail && matchesFilial && matchesCargo && matchesPerfil
            && matchesStatus && matchesAcessoMydash && matchesAcessoSolicitacao;
    }), [usuarios, nomeFilter, emailFilter, filialFilter, cargoFilter, perfilFilter, statusFilter, acessoMydashFilter, acessoSolicitacaoFilter]);

    const limparFiltros = () => {
        setNomeFilter("");
        setEmailFilter("");
        setFilialFilter(null);
        setCargoFilter(null);
        setPerfilFilter([]);
        setStatusFilter("active");
        setAcessoMydashFilter("all");
        setAcessoSolicitacaoFilter("all");
    };

    // Atualiza um usuário na lista local sem recarregar tudo — usado tanto pela alteração otimista
    // quanto pelo rollback em caso de erro nas edições inline (acesso/funcionário).
    function atualizarUsuarioNaLista(id, patch) {
        setUsuarios(prev => prev.map(u => u.id === id ? { ...u, ...patch } : u));
    }

    function alterarAcessoMydash(usuario, novoValor) {
        const anterior = usuario.acessoMydash;
        atualizarUsuarioNaLista(usuario.id, { acessoMydash: novoValor });

        usuarioService.atualizarAcessoMydash(usuario.id, novoValor)
            .then(() => toast.success("Acesso ao MyDash atualizado."))
            .catch(error => {
                console.error("Erro ao atualizar acesso ao MyDash", error);
                atualizarUsuarioNaLista(usuario.id, { acessoMydash: anterior });
                toast.error("Não foi possível atualizar o acesso.");
            });
    }

    function alterarAcessoMydashSolicitacoes(usuario, novoValor) {
        const anterior = usuario.acessoMydashSolicitacoes;
        atualizarUsuarioNaLista(usuario.id, { acessoMydashSolicitacoes: novoValor });

        usuarioService.atualizarAcessoMydashSolicitacoes(usuario.id, novoValor)
            .then(() => toast.success("Acesso ao MyDash Solicitação atualizado."))
            .catch(error => {
                console.error("Erro ao atualizar acesso ao MyDash Solicitação", error);
                atualizarUsuarioNaLista(usuario.id, { acessoMydashSolicitacoes: anterior });
                toast.error("Não foi possível atualizar o acesso.");
            });
    }

    function alterarFuncionario(usuario, funcionarioSelecionado) {
        const anteriorId = usuario.funcionarioId;
        const anteriorNome = usuario.funcionarioNome;
        const anteriorCargo = usuario.funcionarioCargo;
        atualizarUsuarioNaLista(usuario.id, {
            funcionarioId: funcionarioSelecionado?.id ?? null,
            funcionarioNome: funcionarioSelecionado?.nome ?? null,
            funcionarioCargo: funcionarioSelecionado?.cargo ?? null,
        });

        usuarioService.atualizarFuncionario(usuario.id, funcionarioSelecionado?.id ?? null)
            .then(() => toast.success("Funcionário vinculado atualizado."))
            .catch(error => {
                console.error("Erro ao atualizar funcionário vinculado", error);
                atualizarUsuarioNaLista(usuario.id, { funcionarioId: anteriorId, funcionarioNome: anteriorNome, funcionarioCargo: anteriorCargo });
                toast.error("Não foi possível atualizar o funcionário.");
            });
    }

    function openModal(usuario = null) {
        if (usuario) {
            setUsuarioForm({
                ...usuario,
                senha: "",
                confirmSenha: "",
                // null no backend significa "sem restrição" (permitido) — refletir como marcado.
                acessoMydash: usuario.acessoMydash ?? true,
                acessoMydashSolicitacoes: usuario.acessoMydashSolicitacoes ?? true
            });
        } else {
            setUsuarioForm(initialValues);
        }
        setModalOpen(true);
    }

    function handleSave(e) {
        e.preventDefault();
        if (saving) return;

        const userId = usuarioForm.id;
        const nome = usuarioForm.nome;
        const nomeUsuario = usuarioForm.nomeUsuario;
        const email = usuarioForm.email;
        const senha = usuarioForm.senha;
        const confirmSenha = usuarioForm.confirmSenha;
        const ativo = usuarioForm.ativo;
        const filialId = usuarioForm.filialId;

        const perfis = (usuarioForm.perfis || []).map((pLabel) => {
            const foundEntry = Object.entries(PERFIS_MAP).find(
                ([, label]) => label === pLabel
            );
            return foundEntry ? Number(foundEntry[0]) : null;
        }).filter((p) => p !== null);

        if (!nome || !nomeUsuario || !email || (!userId && !senha) || perfis.length === 0) {
            toast.error('Por favor, preencha todos os campos obrigatórios.');
            return;
        }
        if (senha && senha !== confirmSenha) {
            toast.error('As senhas não coincidem.');
            return;
        }

        const dados = {
            nome, nomeUsuario, email, ativo, perfis, filialId,
            tiposProcessoGerenciados: usuarioForm.tiposProcessoGerenciados || [],
            funcionarioId: usuarioForm.funcionarioId || null,
            acessoMydash: usuarioForm.acessoMydash,
            acessoMydashSolicitacoes: usuarioForm.acessoMydashSolicitacoes
        };
        if (senha) dados.senha = senha;

        setSaving(true);
        const request = userId
            ? usuarioService.atualizarUsuario(userId, dados)
            : usuarioService.cadastrarUsuario(dados);

        request
            .then(() => {
                toast.success(userId ? 'Usuário atualizado!' : 'Usuário cadastrado!');
                closeUserModal();
                renderUsersTable();
            })
            .catch(error => {
                console.error("Erro ao salvar usuário", error);
                toast.error("Erro ao salvar usuário");
            })
            .finally(() => setSaving(false));
    }

    const closeUserModal = () => {
        setModalOpen(false);
    };

    function confirmDelete(usuario) {
        setUsuarioDelete(usuario);
        setAtiveModalOpen(true);
    }

    function confirmReset(usuario) {
        setUsuarioDelete(usuario);
        setResetModalOpen(true);
    }

    function handleDelete() {
        usuarioService.ativarOuDesativar(usuarioDelete.id)
            .then(() => {
                toast.success(usuarioDelete.ativo ? 'Usuário inativado!' : 'Usuário ativado!');
                renderUsersTable();
                setAtiveModalOpen(false);
            })
            .catch(error => {
                console.error("Erro ao ativar/desativar usuário", error);
                toast.error("Erro ao ativar/desativar usuário");
            });
    }

    function formatarDataHora(iso) {
        if (!iso) return "-";
        return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
    }

    function carregarSessoes(usuario) {
        setCarregandoSessoes(true);
        usuarioService.listarSessoes(usuario.id)
            .then(res => setSessoes(res.data))
            .catch(error => {
                console.error("Erro ao listar sessões do usuário", error);
                toast.error("Erro ao listar sessões do usuário");
                setSessoes([]);
            })
            .finally(() => setCarregandoSessoes(false));
    }

    function abrirSessoes(usuario) {
        setUsuarioSessoes(usuario);
        setSessoesModalOpen(true);
        carregarSessoes(usuario);
    }

    function handleRevogarSessao(sessaoId) {
        setRevogandoSessaoId(sessaoId);
        usuarioService.revogarSessao(sessaoId)
            .then(() => {
                toast.success("Sessão revogada!");
                carregarSessoes(usuarioSessoes);
            })
            .catch(error => {
                console.error("Erro ao revogar sessão", error);
                toast.error("Erro ao revogar sessão");
            })
            .finally(() => setRevogandoSessaoId(null));
    }

    function handleRevogarTodasSessoes() {
        setRevogandoTodas(true);
        usuarioService.revogarTodasSessoes(usuarioSessoes.id)
            .then(() => {
                toast.success("Todas as sessões foram revogadas!");
                carregarSessoes(usuarioSessoes);
            })
            .catch(error => {
                console.error("Erro ao revogar sessões do usuário", error);
                toast.error("Erro ao revogar sessões do usuário");
            })
            .finally(() => setRevogandoTodas(false));
    }

    function handleReset() {
        usuarioService.resetarSenha(usuarioDelete.id)
            .then(() => {
                toast.success('Senha do usuário foi redefinida!');
                setResetModalOpen(false);
            })
            .catch(error => {
                console.error("Erro ao resetar senha do usuário", error);
                toast.error("Erro ao resetar senha do usuário");
            });
    }

    const menuItems = [
        {
            label: 'Editar',
            icon: 'fa fa-edit w-4',
            command: () => openModal(selectedUser)
        },
        {
            label: selectedUser?.ativo ? "Bloquear" : "Ativar",
            icon: selectedUser?.ativo ? "fa fa-lock" : "fa fa-unlock",
            command: () => confirmDelete(selectedUser)
        },
        {
            label: "Resetar Senha",
            icon: "fa fa-key",
            command: () => confirmReset(selectedUser)
        },
        {
            label: "Sessões",
            icon: "fa fa-desktop",
            command: () => abrirSessoes(selectedUser)
        }
    ];

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="mx-auto overflow-hidden rounded-2xl border border-border bg-card shadow-card">
                {/* Header */}
                <div className="flex flex-col gap-4 bg-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-7">
                    <h1 className="font-display text-2xl font-bold text-primary-foreground">Configuração de Usuários</h1>
                    <div className="flex gap-2">
                        <button
                            onClick={() => openModal()}
                            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-sm font-semibold text-primary transition-colors duration-150 hover:bg-muted"
                        >
                            <i className="fas fa-plus mr-2"></i> Novo Usuário
                        </button>
                    </div>
                </div>

                {/* Search & Filter */}
                <div className="border-b border-border bg-muted px-5 py-5 md:px-7">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        <CampoFiltro label="Pesquisar">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Nome ou usuário"
                                    value={nomeFilter}
                                    onChange={e => setNomeFilter(e.target.value)}
                                    className="w-full h-9 pl-8 pr-3 text-sm border border-input rounded-lg bg-card transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                                />
                                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"></i>
                            </div>
                        </CampoFiltro>

                        <CampoFiltro label="E-mail">
                            <input
                                type="email"
                                placeholder="Buscar por e-mail"
                                value={emailFilter}
                                onChange={e => setEmailFilter(e.target.value)}
                                className="w-full h-9 px-3 text-sm border border-input rounded-lg bg-card transition-colors duration-150 focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none"
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Filial">
                            <Dropdown
                                options={filiaisOptions}
                                optionLabel="label"
                                optionValue="value"
                                value={filialFilter}
                                showClear
                                filter
                                placeholder="Todas as filiais"
                                className="w-full border"
                                onChange={e => setFilialFilter(e.value)}
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Cargo">
                            <Dropdown
                                options={cargosOptions}
                                optionLabel="label"
                                optionValue="value"
                                value={cargoFilter}
                                showClear
                                filter
                                placeholder="Todos os cargos"
                                className="w-full border"
                                onChange={e => setCargoFilter(e.value)}
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Perfis">
                            <MultiSelect
                                value={perfilFilter}
                                options={perfisOptions}
                                filter
                                display="chip"
                                placeholder="Todos os perfis"
                                className="w-full border"
                                onChange={e => setPerfilFilter(e.value || [])}
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Status">
                            <Dropdown
                                options={[
                                    { label: 'Todos', value: 'all' },
                                    { label: 'Ativos', value: 'active' },
                                    { label: 'Inativos', value: 'inactive' }
                                ]}
                                optionLabel="label"
                                optionValue="value"
                                value={statusFilter}
                                className="w-full border"
                                onChange={e => setStatusFilter(e.target.value)}
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Acesso MyDash">
                            <Dropdown
                                options={[
                                    { label: 'Todos', value: 'all' },
                                    { label: 'Com acesso', value: 'yes' },
                                    { label: 'Sem acesso', value: 'no' }
                                ]}
                                optionLabel="label"
                                optionValue="value"
                                value={acessoMydashFilter}
                                className="w-full border"
                                onChange={e => setAcessoMydashFilter(e.target.value)}
                            />
                        </CampoFiltro>

                        <CampoFiltro label="Acesso MyDash Solicitação">
                            <Dropdown
                                options={[
                                    { label: 'Todos', value: 'all' },
                                    { label: 'Com acesso', value: 'yes' },
                                    { label: 'Sem acesso', value: 'no' }
                                ]}
                                optionLabel="label"
                                optionValue="value"
                                value={acessoSolicitacaoFilter}
                                className="w-full border"
                                onChange={e => setAcessoSolicitacaoFilter(e.target.value)}
                            />
                        </CampoFiltro>
                    </div>

                    <div className="flex justify-end mt-3">
                        <button
                            type="button"
                            onClick={limparFiltros}
                            className="whitespace-nowrap text-xs font-semibold text-primary transition-colors duration-150 hover:text-primary/80"
                        >
                            <i className="fa fa-eraser mr-1.5" />
                            Limpar filtros
                        </button>
                    </div>
                </div>

                {/* Table */}
                {loading ?
                    <Loading />
                    :
                    <div className="border-b border-border">
                        <div className="overflow-x-auto fade-in">
                            <table className="min-w-full divide-y divide-border">
                                <thead className="bg-table-header">
                                    <tr className="text-sm">
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Usuário</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Funcionário</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Cargo</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Login</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Último Login</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Filial</th>
                                        <th className="py-2 px-3 text-left text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Perfis</th>
                                        <th className="py-2 px-3 text-center text-[10px] font-medium text-table-header-fg uppercase tracking-wider">MyDash</th>
                                        <th className="py-2 px-3 text-center text-[10px] font-medium text-table-header-fg uppercase tracking-wider">MyDash Sol.</th>
                                        <th className="py-2 px-3 text-center text-[10px] font-medium text-table-header-fg uppercase tracking-wider">Ações</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border bg-card">
                                    {paginatedUsuarios.length === 0 ? (
                                        <tr className="table-items">
                                            <td colSpan="10" className="px-6 py-3 text-center text-muted-foreground">
                                                Nenhum usuário encontrado
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedUsuarios.map(u => (
                                            <tr key={u.id} className="table-items text-xs transition-colors duration-150 hover:bg-table-row-hover">
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${u.ativo ? 'text-primary bg-primary/10' : 'text-destructive bg-destructive/10'}`}>
                                                            <span className="text-xs font-medium">{u.nome.charAt(0).toUpperCase()}</span>
                                                        </div>
                                                        <div className="ml-2.5 min-w-0">
                                                            <div className="text-xs font-medium text-foreground truncate max-w-[160px]" title={u.nome}>{reduzirNome(u.nome)}</div>
                                                            <span className={`mt-0.5 inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-semibold ${u.ativo ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
                                                                {u.ativo ? "Ativo" : "Inativo"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <FuncionarioCelula usuario={u} onSalvar={f => alterarFuncionario(u, f)} />
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground truncate max-w-[130px]" title={u.funcionarioCargo || "-"}>{u.funcionarioCargo || "—"}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{u.nomeUsuario}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{formatarDataHora(u.ultimoLogin)}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground truncate max-w-[120px]" title={u.filialNome || "-"}>{u.filialNome || "-"}</td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                                                        {u.perfis?.map(x => (
                                                            <label key={x} className="inline-flex rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold text-primary">
                                                                {x.toLowerCase() || ''}
                                                            </label>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                                    <InputSwitch
                                                        checked={u.acessoMydash ?? true}
                                                        onChange={e => alterarAcessoMydash(u, e.value)}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                                    <InputSwitch
                                                        checked={u.acessoMydashSolicitacoes ?? true}
                                                        onChange={e => alterarAcessoMydashSolicitacoes(u, e.value)}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap text-center">
                                                    <div className="flex justify-center">
                                                        <Button
                                                            variant="gray"
                                                            iconLeft="fa fa-bars"
                                                            aria-label="Abrir menu de ações"
                                                            onClick={(e) => {
                                                                setSelectedUser(u);
                                                                menu.current.toggle(e);
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                }

                {/* Menu único de ações — fora do loop para não duplicar instâncias por linha */}
                <Menu model={menuItems} popup ref={menu} />

                {/* Pagination */}
                <div className="px-4 py-3 border-t border-border">
                    <Pagination
                        data={filteredUsuarios}
                        loading={loading}
                        currentData={setPaginatedUsuarios}
                        initialPageSize={25}
                        pageSizeOptions={[10, 25, 50, 100]}
                    />
                </div>
            </div>

            {/* Modal Form */}
            <Dialog
                header="Cadastro de Usuário"
                onHide={closeUserModal}
                visible={modalOpen}
                blockScroll
                className="w-[90%]"
                footer={
                    <div className="flex justify-end space-x-3 mt-3">
                        <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 bg-card border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                        <button
                            type="submit"
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 bg-primary hover:brightness-95 text-primary-foreground rounded-md transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {saving && <i className="fas fa-spinner fa-spin"></i>}
                            {saving ? "Salvando..." : "Salvar"}
                        </button>
                    </div>
                }
            >
                <form className="p-6" onSubmit={handleSave}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 overflow-y-auto max-h-[100vh] p-1 pb-4">
                        <InputForm type="text" label="Nome Completo" placeholder="Nome Completo" value={usuarioForm.nome} onChange={e => setUsuarioForm({ ...usuarioForm, nome: e.target.value })} className="w-full mb-4 border px-3 py-2" />
                        <InputForm type="text" label="Nome de Usuário" value={usuarioForm.nomeUsuario} onChange={e => setUsuarioForm({ ...usuarioForm, nomeUsuario: e.target.value })} className="w-full mb-4 border px-3 py-2" />
                        <InputForm type="email" label="E-mail" value={usuarioForm.email} onChange={e => setUsuarioForm({ ...usuarioForm, email: e.target.value })} className="w-full mb-4 border px-3 py-2" />

                        <div>
                            <label className="block text-sm font-medium text-foreground/80 mb-1">
                                Filial
                            </label>
                            <Dropdown
                                className="w-full text-xs border"
                                optionLabel="nome"
                                optionValue="id"
                                name="filialId"
                                options={filiais}
                                value={usuarioForm.filialId}
                                filter={false}
                                placeholder="Selecione a filial"
                                onChange={e => setUsuarioForm({ ...usuarioForm, filialId: e.value })}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-foreground/80 mb-1">
                                Funcionário
                            </label>
                            <FuncionarioAutoComplete
                                value={usuarioForm.funcionarioId ? { id: usuarioForm.funcionarioId, nome: usuarioForm.funcionarioNome } : null}
                                onChange={funcionario => setUsuarioForm({
                                    ...usuarioForm,
                                    funcionarioId: funcionario?.id ?? null,
                                    funcionarioNome: funcionario?.nome ?? "",
                                })}
                                placeholder="Buscar funcionário..."
                            />
                        </div>

                        {/* Corrigido: parênteses garantem que os campos de senha
                            apareçam para usuário NOVO (id null/""), como era a intenção original.
                            Antes, por precedência de operadores (&& antes de ||),
                            a expressão virava um booleano em vez do JSX e os campos nunca apareciam. */}
                        {isNovoUsuario && (
                            <>
                                <InputForm type="password" label="Senha" value={usuarioForm.senha} onChange={e => setUsuarioForm({ ...usuarioForm, senha: e.target.value })} className="w-full mb-4 border px-3 py-2" />
                                <InputForm type="password" label="Confirmar Senha" value={usuarioForm.confirmSenha} onChange={e => setUsuarioForm({ ...usuarioForm, confirmSenha: e.target.value })} className="w-full mb-4 border px-3 py-2" />
                            </>
                        )}

                        <div className="col-span-3 mb-4">
                            <label className="block text-sm font-medium text-foreground/80 mb-2">
                                Perfis*
                            </label>
                            <MultiSelect
                                value={usuarioForm.perfis}
                                options={perfisOptions}
                                filter
                                onChange={(e) =>
                                    setUsuarioForm((prev) => ({
                                        ...prev,
                                        perfis: e.value || [],
                                    }))
                                }
                                display="chip"
                                placeholder="Selecione os perfis"
                                className="w-full border border-input rounded-xl shadow-xs focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                            />
                        </div>

                        <div className="col-span-3 mb-4">
                            <label className="block text-sm font-medium text-foreground/80 mb-2">
                                Gestor dos Processos
                            </label>
                            <MultiSelect
                                value={usuarioForm.tiposProcessoGerenciados}
                                options={gestorProcessoOptions}
                                filter
                                onChange={(e) =>
                                    setUsuarioForm((prev) => ({
                                        ...prev,
                                        tiposProcessoGerenciados: e.value || [],
                                    }))
                                }
                                display="chip"
                                placeholder="Nenhum — vê só o que participar"
                                className="w-full border border-input rounded-xl shadow-xs focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                            />
                        </div>

                        <div className="col-span-3 mb-4">
                            <label className="block text-sm font-medium text-foreground/80 mb-2">
                                Acesso aos Aplicativos
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 flex-1">
                                    <InputSwitch
                                        checked={usuarioForm.acessoMydash}
                                        onChange={e => setUsuarioForm({ ...usuarioForm, acessoMydash: e.value })}
                                    />
                                    <span className="text-sm text-foreground">MyDash</span>
                                </div>
                                <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2 flex-1">
                                    <InputSwitch
                                        checked={usuarioForm.acessoMydashSolicitacoes}
                                        onChange={e => setUsuarioForm({ ...usuarioForm, acessoMydashSolicitacoes: e.value })}
                                    />
                                    <span className="text-sm text-foreground">MyDash Solicitações</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </Dialog>

            {/* Delete/Ativar Confirmation */}
            {ativeModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Tem certeza que deseja <strong>{usuarioDelete.ativo ? "INATIVAR" : "ATIVAR"}</strong> usuário: {usuarioDelete?.nome}?</p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setAtiveModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={handleDelete} className={`px-4 py-2 rounded-md transition-all duration-150 ${usuarioDelete.ativo ? "bg-destructive text-destructive-foreground hover:brightness-95" : "bg-primary text-primary-foreground hover:brightness-95"}`}>{usuarioDelete.ativo ? "Inativar" : "Ativar"}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Confirmation */}
            {resetModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-card rounded-lg shadow-pop w-full max-w-md p-6">
                        <p className="text-foreground">Tem certeza que deseja resetar a senha deste usuário?</p>
                        <div className="flex justify-end space-x-3 mt-4">
                            <button onClick={() => setResetModalOpen(false)} className="px-4 py-2 border border-input rounded-md transition-colors duration-150 hover:bg-muted">Cancelar</button>
                            <button onClick={handleReset} className="px-4 py-2 text-primary-foreground rounded-md bg-primary hover:brightness-95 transition-all duration-150">Resetar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sessões (dispositivos logados) */}
            <Dialog
                header={`Sessões de ${usuarioSessoes?.nome || ""}`}
                visible={sessoesModalOpen}
                onHide={() => setSessoesModalOpen(false)}
                className="w-[95%] max-w-2xl"
            >
                <div className="flex flex-col gap-3 p-1">
                    <p className="text-xs text-muted-foreground">
                        Dispositivos com login ativo (sessão não revogada e ainda dentro da validade). Revogar uma
                        sessão não desconecta na hora — o usuário fica sem conseguir renovar o acesso assim que ele expirar.
                    </p>

                    {carregandoSessoes ? (
                        <Loading />
                    ) : sessoes.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">Nenhuma sessão ativa.</p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {sessoes.map(s => (
                                <div key={s.id} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs text-foreground truncate" title={s.userAgent || "-"}>{s.userAgent || "Dispositivo desconhecido"}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                            Login em {formatarDataHora(s.criadoEm)} · IP {s.ip || "-"} · expira em {formatarDataHora(s.expiraEm)}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleRevogarSessao(s.id)}
                                        disabled={revogandoSessaoId === s.id}
                                        className="shrink-0 px-3 py-1.5 text-xs rounded-md bg-destructive text-destructive-foreground hover:brightness-95 transition-all duration-150 disabled:opacity-60"
                                    >
                                        {revogandoSessaoId === s.id ? "Revogando..." : "Revogar"}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <button
                            onClick={handleRevogarTodasSessoes}
                            disabled={sessoes.length === 0 || revogandoTodas}
                            className="px-4 py-2 text-sm rounded-md bg-destructive text-destructive-foreground hover:brightness-95 transition-all duration-150 disabled:opacity-60"
                        >
                            {revogandoTodas ? "Revogando..." : "Revogar todas as sessões"}
                        </button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
